import { useTranslation } from 'src/i18next-arb-shim/useTranslation';
import {
  SourceString,
  useUpdateStringsInStringGroupMutation,
} from 'src/generated/graphql';
import './UpdateStringsModal.scss';
import { toSourceStringInputs } from 'src/views/SourceReview/MessageGroupDetailView/Utils';
import React, { useContext, useEffect, useState } from 'react';
import { KatButton, KatModal, KatSpinner } from '@amzn/katal-react';
import { AuthContext } from 'src/components/auth/AuthContext';

type Props = {
  isOpen: boolean;
  updatedStrings: Record<string, any>;
  stringResources: Map<string, SourceString>;
  onClose: () => void;
  onSuccess?: () => void;
};

const errorMessageReplacements: Record<string, string> = {
  'Resource Parsing Exception: Unable to process data.':
    'source-review-update-strings-message-format-error',
  'Resource Parsing Exception: Failed to create a parser for complex resource content.':
    'source-review-update-strings-message-format-error',
  'Resource Parsing Exception: Failed to parse json for complex resource content.':
    'source-review-update-strings-message-format-error',
  'Resource Parsing Exception: Invalid JSON start token for complex resource content.':
    'source-review-update-strings-message-format-error',
  'Resource Parsing Exception: Items field is required for complex resource content.':
    'source-review-update-strings-missing-required-fields-error',
  'Resource Parsing Exception: Param field is required for complex resource content.':
    'source-review-update-strings-missing-required-fields-error',
  'Resource Parsing Exception: Unexpected field name for complex resource content.':
    'source-review-update-strings-unexpected-field-error',
};

const replaceErrorMessage = (
  message: string,
  t: (key: string, params?: any) => string
): string => {
  // Find message Id in validation error message
  const messageIdMatch = message.match(/messageId=\[([^\]]+)\]/);
  const messageId = messageIdMatch ? messageIdMatch[1] : '';

  // Get base message to be translated
  const baseMessage = messageIdMatch
    ? message.replace(messageIdMatch[0], '').trim()
    : message;
  const translationKey = errorMessageReplacements[baseMessage];

  // Translate known error messages
  if (translationKey) {
    return t(translationKey, {
      messageId: messageId,
      a: (chunks: any) => (
        <a
          href="https://w.amazon.com/bin/view/INTech/Panther/Architecture/PantherFileFormat/JSON#HStructuredresourcevaluetypes"
          target="_blank"
          rel="noreferrer"
        >
          {chunks}
        </a>
      ),
    });
  }

  return message;
};

const UpdateStringsModal: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const { userId } = useContext(AuthContext);
  const [updateStringsInStringGroup, { reset, error }] =
    useUpdateStringsInStringGroupMutation();

  const [hasUpdated, setHasUpdated] = useState(false);

  const [validationErrorMessages, setValidationErrorMessages] = useState<
    (string | null)[] | undefined | null
  >([]);

  // Reset state when the modal opens or closes
  useEffect(() => {
    if (props.isOpen && !hasUpdated) {
      setHasUpdated(true);
      reset();
      setValidationErrorMessages([]);
      updateStringsInStringGroup({
        variables: {
          params: {
            strings: toSourceStringInputs(
              props.updatedStrings,
              props.stringResources,
              userId
            ),
          },
        },
        errorPolicy: 'all',
      })
        .then((response) => {
          if (!response.errors) {
            // Set the error messages to display
            setValidationErrorMessages(
              response.data?.updateStringsInStringGroup?.validationErrorMessages
            );

            if (response.data?.updateStringsInStringGroup?.validationSuccess) {
              if (props.onSuccess) {
                props.onSuccess();
              }
              props.onClose();
            }
          }
        })
        .catch((error) => {
          console.log('Error updating the strings: ', error);
        });
    } else if (!props.isOpen) {
      setHasUpdated(false);
    }
  }, [props, reset, updateStringsInStringGroup, userId, hasUpdated]);

  if (validationErrorMessages && validationErrorMessages.length > 0) {
    return (
      <KatModal
        title={t('source-review-update-strings-failed-title')}
        visible={props.isOpen}
        onClose={props.onClose}
        className="update-strings-error-modal"
      >
        <div className="update-strings-container">
          <div>{t('source-review-update-strings-failed-message')}</div>
          <div className="update-strings-text">
            <ul className="update-strings-error-message-list">
              {validationErrorMessages.map((item, index) => (
                <li key={index}>{replaceErrorMessage(item || '', t)}</li>
              ))}
            </ul>
          </div>
        </div>
        <div slot="footer">
          <KatButton
            id="update-strings-dismiss"
            label={t('source-review-update-strings-failed-button')}
            variant="primary"
            onClick={props.onClose}
          />
        </div>
      </KatModal>
    );
  }

  if (error) {
    // Show custom error message for 'string group already submitted for translation'
    // and 'string group in cancelling state'
    let errorMessage;
    if (
      error.message.includes(
        'The current string group review state: CANCELLING'
      )
    ) {
      errorMessage = t(
        'source-review-update-strings-ongoing-cancellation-error'
      );
    } else if (error.message.includes('has no open source review')) {
      errorMessage = t(
        'source-review-string-group-already-submitted-display-error-message'
      );
    } else {
      errorMessage = t('source-review-update-strings-server-error-message', {
        a: (chunks: any) => (
          <a
            href="https://w.amazon.com/bin/view/LINX/Contact_Us/"
            target="_blank"
            rel="noreferrer"
          >
            {chunks}
          </a>
        ),
      });
    }

    return (
      <KatModal
        title={t('source-review-update-strings-failed-title')}
        visible={props.isOpen}
        onClose={props.onClose}
        className="update-strings-error-modal"
      >
        <div className="update-strings-container">
          <div className="update-strings-text">{errorMessage}</div>
        </div>
        <div slot="footer">
          <KatButton
            id="update-strings-dismiss"
            label={t('source-review-update-strings-failed-button')}
            variant="primary"
            onClick={props.onClose}
          />
        </div>
      </KatModal>
    );
  }

  return (
    <KatModal
      noCloseIcon={true}
      visible={props.isOpen}
      className="update-strings-modal"
    >
      <div className="update-strings-loading-container">
        <KatSpinner size="small" />
        <div className="update-strings-text">
          {t('source-review-update-strings-loading')}
        </div>
      </div>
    </KatModal>
  );
};

export default UpdateStringsModal;
